import { useJurisdictions } from '@commenda/api';
import { Badge } from '@commenda/kakashi';

import { OwnershipDistributionBar } from './OwnershipDistributionBar';
import {
  OwnershipSummaryHelper,
  type OwnershipSummaryValue,
} from './OwnershipSummaryHelper';
import { OwnershipSummaryMetrics } from './OwnershipSummaryMetrics';
import { OwnershipSummaryOwnersTable } from './OwnershipSummaryOwnersTable';
import type { PartnershipStructure } from './types';
import { useEntitySignatories } from './useEntitySignatories';

interface OwnershipSummaryViewProps {
  title?: string;
  value: unknown;
  // The node's configured structure, for values saved before it was persisted
  // into the value itself.
  partnershipStructure?: PartnershipStructure;
  // Template of the entity owner's details sub-form, where its signatory is picked.
  entityDetailsKeyId?: string;
  // Host form answers the signatory picker sources people from.
  formData?: Record<string, unknown>;
}

export function OwnershipSummaryView({
  title,
  value,
  partnershipStructure,
  entityDetailsKeyId,
  formData,
}: OwnershipSummaryViewProps) {
  const { labelOf } = useJurisdictions();
  // Callers gate on isOwnershipValue; every field read below is optional.
  const ownershipValue = (value ?? {}) as OwnershipSummaryValue;
  const model = OwnershipSummaryHelper.buildModel(
    ownershipValue,
    labelOf,
    partnershipStructure
  );
  const signatoriesByOwner = useEntitySignatories({
    entityDetailsKeyId,
    formData,
    value: ownershipValue,
  });

  return (
    <section className="flex flex-col gap-xl rounded-xl border border-border-primary bg-bg-primary p-xl">
      <header className="flex flex-wrap items-center justify-between gap-md">
        {title ? (
          <h4 className="txt-medium-bold text-text-primary">{title}</h4>
        ) : null}
        <Badge variant="gray" size="md">
          {model.holdingTypeLabel}
        </Badge>
      </header>

      <OwnershipSummaryMetrics metrics={model.metrics} />

      {model.owners.length === 0 ? (
        <span className="txt-small-regular text-text-tertiary">
          No owners added.
        </span>
      ) : (
        <>
          <OwnershipDistributionBar
            owners={model.owners}
            percentTotal={model.percentTotal}
          />
          <OwnershipSummaryOwnersTable
            owners={model.owners}
            columns={model.columns}
            totals={model.totals}
            signatoriesByOwner={signatoriesByOwner}
          />
        </>
      )}
    </section>
  );
}
